"use client";

import { FormEvent, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { getApiUrl } from "@/lib/api";
import styles from "./hoc-tap-login-modal.module.css";

export function openHocTapLoginModal() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("open-hoc-tap-login"));
  }
}

type LoginStatus = {
  message: string;
  tone: "error" | "info" | "success";
};

type AuthResponse = {
  status?: string;
  message?: string;
  data?: {
    token?: string;
    member?: unknown;
  };
};

type HocTapLoginModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function HocTapLoginModal({ isOpen, onClose }: HocTapLoginModalProps) {
  const router = useRouter();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotLoading, setIsForgotLoading] = useState(false);
  const [status, setStatus] = useState<LoginStatus | null>(null);

  const identifierInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const apiUrl = getApiUrl();

  // Reset & focus management when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStatus(null);
      document.body.style.overflow = "hidden";
      const timer = setTimeout(() => {
        identifierInputRef.current?.focus();
      }, 50);

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          onClose();
        }
      };

      document.addEventListener("keydown", handleKeyDown);

      return () => {
        clearTimeout(timer);
        document.body.style.overflow = "";
        document.removeEventListener("keydown", handleKeyDown);
      };
    } else {
      document.body.style.overflow = "";
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!identifier.trim() || !password.trim()) {
      setStatus({
        message: "Vui lòng nhập Email / Mã số sinh viên và Mật khẩu.",
        tone: "error",
      });
      return;
    }

    setIsLoading(true);
    setStatus(null);

    try {
      const response = await fetch(`${apiUrl}/api/v1/member/auth/login`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          identifier: identifier.trim(),
          password: password.trim(),
        }),
      });

      const data = (await response.json()) as AuthResponse;

      if (!response.ok) {
        throw new Error(
          data.message || "Tài khoản hoặc mật khẩu không chính xác."
        );
      }

      if (!data.data?.token) {
        throw new Error("Phản hồi đăng nhập không hợp lệ.");
      }

      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.setItem("member_token", data.data.token);
      localStorage.setItem(
        "member_user",
        JSON.stringify(data.data.member ?? null)
      );
      window.dispatchEvent(new Event("auth-change"));

      setStatus({
        message: "Đăng nhập thành công! Đang chuyển hướng về Dashboard...",
        tone: "success",
      });

      window.setTimeout(() => {
        onClose();
        router.push("/hoc-tap/dashboard");
      }, 600);
    } catch (error: unknown) {
      setStatus({
        message:
          error instanceof Error
            ? error.message
            : "Không thể kết nối đến máy chủ. Vui lòng thử lại sau.",
        tone: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!identifier.trim()) {
      setStatus({
        message:
          "Vui lòng nhập Email hoặc Mã số sinh viên vào ô bên trên để nhận email đổi mật khẩu.",
        tone: "error",
      });
      identifierInputRef.current?.focus();
      return;
    }

    setIsForgotLoading(true);
    setStatus({
      message: "Đang kiểm tra hệ thống và gửi email đổi mật khẩu...",
      tone: "info",
    });

    try {
      const response = await fetch(
        `${apiUrl}/api/v1/member/auth/forgot-password`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            identifier: identifier.trim(),
          }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data.message ||
            "Không thể gửi email đổi mật khẩu. Vui lòng thử lại sau."
        );
      }

      setStatus({
        message:
          data.message ||
          "Hệ thống đã gửi email hướng dẫn đổi mật khẩu! Vui lòng kiểm tra hộp thư của bạn.",
        tone: "success",
      });
    } catch (err: unknown) {
      setStatus({
        message:
          err instanceof Error
            ? err.message
            : "Đã có lỗi xảy ra khi gửi email đổi mật khẩu. Vui lòng thử lại sau.",
        tone: "error",
      });
    } finally {
      setIsForgotLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-hoc-tap-title"
      onClick={handleBackdropClick}
    >
      <div className={styles.modalCard} ref={modalRef}>
        <button
          type="button"
          className={styles.closeButton}
          aria-label="Đóng popup đăng nhập"
          onClick={onClose}
        >
          ✕
        </button>

        <div className={styles.header}>
          <div className={styles.iconBadge} aria-hidden="true">
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
              <path d="M6 12v5c3 3 9 3 12 0v-5" />
            </svg>
          </div>
          <h2 className={styles.title} id="modal-hoc-tap-title">
            Đăng nhập Cổng Đào Tạo
          </h2>
          <p className={styles.subtitle}>
            Dành cho sinh viên tham gia khóa học trực tuyến Panasonic
          </p>
        </div>

        {status && (
          <div
            className={`${styles.alert} ${
              status.tone === "error"
                ? styles.alertError
                : status.tone === "success"
                ? styles.alertSuccess
                : styles.alertInfo
            }`}
            role="status"
            aria-live="polite"
          >
            {status.message}
          </div>
        )}

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.field}>
            <label htmlFor="popup-member-identifier">
              Tài khoản (Mã SV hoặc Email)
            </label>
            <div className={styles.inputWrap}>
              <input
                id="popup-member-identifier"
                name="identifier"
                type="text"
                ref={identifierInputRef}
                autoComplete="username"
                placeholder="Nhập mã sinh viên hoặc Email"
                value={identifier}
                disabled={isLoading}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.field}>
            <label htmlFor="popup-member-password">Mật khẩu</label>
            <div className={styles.inputWrap}>
              <input
                id="popup-member-password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Nhập mật khẩu của bạn"
                value={password}
                disabled={isLoading}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={`${styles.input} ${styles.passwordInput}`}
              />
              <button
                type="button"
                className={styles.togglePasswordBtn}
                aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                onClick={() => setShowPassword((prev) => !prev)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className={styles.formOptions}>
            <label className={styles.checkboxLabel}>
              <input
                name="remember"
                type="checkbox"
                disabled={isLoading || isForgotLoading}
              />
              <span>Lưu mật khẩu</span>
            </label>
            <button
              className={styles.forgotBtn}
              type="button"
              disabled={isLoading || isForgotLoading}
              onClick={handleForgotPassword}
            >
              {isForgotLoading ? "Đang gửi email..." : "Quên mật khẩu?"}
            </button>
          </div>

          <label className={styles.termsLabel}>
            <input
              name="terms"
              type="checkbox"
              defaultChecked
              disabled={isLoading}
              required
            />
            <span>
              Tôi đồng ý với{" "}
              <a
                href="https://docs.google.com/document/d/1DzcrySd5lo7bIo1YzEIWNLjY2NYw6FdIij_hBObqklE/edit?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
              >
                Điều khoản sử dụng và Chính sách bảo vệ dữ liệu cá nhân của Panasonic
              </a>
            </span>
          </label>

          <button
            className={styles.submitBtn}
            type="submit"
            disabled={isLoading}
            aria-busy={isLoading}
          >
            {isLoading ? (
              <span className={styles.loadingSpinnerWrap}>
                <span className={styles.spinner} aria-hidden="true" />
                Đang đăng nhập...
              </span>
            ) : (
              "Đăng nhập"
            )}
          </button>

          <p className={styles.registerFooter}>
            Chưa có tài khoản?{" "}
            <Link href="/hoc-tap/register" onClick={onClose}>
              Đăng ký thí sinh tự do
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
